import { DEFAULT_COMPANY_TIME_ZONE, formatTry, zonedParts, type PlatformPaymentRowDto, type PlatformPaymentsDto } from '@dbrain/shared';
import { Badge, Button, Dialog, Field, Icons, Menu, MenuItem, Skeleton, TextInput } from '@dbrain/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { usePayments } from '../../api/billing.js';
import { api, upload as uploadFile } from '../../api/client.js';
import { CsvLink, DataTable, FilterChips, FilterMenu, Pagination, SearchBox, useListParams, type Column } from '../../components/admin/DataTable.js';
import { ErrorAlert } from '../../components/ErrorAlert.js';

/**
 * Platform › Abonelikler ve ödemeler (Phase 16c, B2cPlatformOdemeler): the month's tiles, the payments under chips
 * with their counts, a month and a search, each row's invoice number and PDF ("Kesilecek" until both are there) and a
 * refund, and the month's export for accounting. Card numbers are never here: the provider's payment number only.
 */
const KEY = ['platform', 'payments'] as const;
const ZONE = DEFAULT_COMPANY_TIME_ZONE;

/** The last twelve months, newest first (YYYY-MM, Istanbul) */
export function recentMonths(now = new Date(), count = 12): string[] {
  const p = zonedParts(now, ZONE);
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const index = p.year * 12 + (p.month - 1) - i;
    out.push(`${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`);
  }
  return out;
}

const monthName = (month: string, locale: string) => {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1, 15)).toLocaleDateString(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

export function usePaymentsQuery() {
  const list = useListParams<'status' | 'month'>({});
  const query = list.query();
  const payments = useQuery({ queryKey: [...KEY, query], queryFn: () => api<PlatformPaymentsDto>('GET', `/api/platform/payments?${query}`), placeholderData: keepPreviousData });
  return { list, payments, query };
}

/** "Muhasebe için dışa aktar (CSV)": the page's month and filters */
export function PaymentsCsvButton() {
  const { t } = useTranslation();
  const list = useListParams<'status' | 'month'>({});
  const query = new URLSearchParams([...new URLSearchParams(list.query())].filter(([key]) => key !== 'page' && key !== 'size')).toString();
  return <CsvLink href={`/api/platform/payments.csv${query ? `?${query}` : ''}`} label={t('b2cBilling.platform.csv')} testId="payments-csv" />;
}

function Tile({ title, value, sub, testId }: { title: string; value: ReactNode; sub: ReactNode; testId: string }) {
  return (
    <section className="stat-card" aria-label={title} data-testid={testId}>
      <h2 className="stat-card__label">{title}</h2>
      <p className="stat-card__value">{value}</p>
      <p className="stat-card__sub">{sub}</p>
    </section>
  );
}

export function PaymentsTab() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { list, payments } = usePaymentsQuery();
  const providerOpen = usePayments().open;
  const [numbering, setNumbering] = useState<PlatformPaymentRowDto | null>(null);
  const [refunding, setRefunding] = useState<PlatformPaymentRowDto | null>(null);
  const [uploading, setUploading] = useState<PlatformPaymentRowDto | null>(null);
  const [error, setError] = useState<unknown>(null);
  const queryClient = useQueryClient();
  const file = useRef<HTMLInputElement>(null);
  const data = payments.data;
  const summary = data?.summary;
  const month = list.get('month') ?? summary?.month;
  const status = list.get('status') as 'paid' | 'failed' | 'refunded' | undefined;
  const refresh = () => void queryClient.invalidateQueries({ queryKey: KEY });
  const when = (iso: string) =>
    `${new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'short', timeZone: ZONE })} ${new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone: ZONE })}`;
  const day = (iso: string) => new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'short', timeZone: ZONE });

  const upload = async (row: PlatformPaymentRowDto, chosen: File) => {
    setError(null);
    try {
      if (!row.invoice) return;
      await uploadFile('PUT', `/api/platform/invoices/${row.invoice.id}/pdf`, chosen, { 'content-type': 'application/pdf' });
      refresh();
    } catch (failure) {
      setError(failure);
    } finally {
      setUploading(null);
    }
  };

  const paymentCell = (row: PlatformPaymentRowDto) => {
    const tone = row.status === 'paid' ? 'success' : row.status === 'failed' ? 'danger' : 'neutral';
    const detail =
      row.status === 'failed'
        ? row.final
          ? t('b2cBilling.platform.failedFinal')
          : [row.failureText, row.nextRetryAt ? t('b2cBilling.platform.retryOn', { n: row.retries + 1, date: day(row.nextRetryAt) }) : null].filter(Boolean).join(' · ')
        : row.status === 'refunded'
          ? [row.refundedAt ? day(row.refundedAt) : null, row.refundReason].filter(Boolean).join(' · ')
          : null;
    return (
      <span className="payment-cell">
        <Badge tone={tone}>{t(`b2cBilling.platform.status.${row.status}`)}</Badge>
        {detail ? <span className="payment-cell__detail">{detail}</span> : null}
      </span>
    );
  };

  const columns: Column<PlatformPaymentRowDto>[] = [
    { id: 'date', header: t('b2cBilling.platform.columns.date'), width: 'sm', cell: (row) => <time dateTime={row.createdAt}>{when(row.createdAt)}</time> },
    {
      id: 'person',
      header: t('b2cBilling.platform.columns.person'),
      width: 'lg',
      cell: (row) => (
        <span className="person-cell">
          <span className="person-cell__name">{row.name}</span>
          <span className="person-cell__email">{row.email}</span>
        </span>
      ),
    },
    // the fixed columns, the payment's least width and the menu fit a 1280-pixel window (916 of its 920)
    { id: 'plan', header: t('b2cBilling.platform.columns.plan'), width: 'sm', cell: (row) => t(`b2cBilling.periods.pro.${row.period}`) },
    { id: 'amount', header: t('b2cBilling.platform.columns.amount'), width: 'sm', numeric: true, cell: (row) => formatTry(row.amountKurus, locale) },
    { id: 'payment', header: t('b2cBilling.platform.columns.payment'), cell: paymentCell },
    {
      id: 'invoice',
      header: t('b2cBilling.platform.columns.invoice'),
      width: 'md',
      cell: (row) =>
        !row.invoice ? (
          <span className="muted">—</span>
        ) : row.invoice.status === 'issued' ? (
          <span className="invoice-table__number">{row.invoice.number}</span>
        ) : (
          <span className="invoice-cell__pending" data-testid="invoice-pending">
            {row.invoice.number ? `${row.invoice.number} · ${t('b2cBilling.platform.noPdf')}` : t('b2cBilling.platform.toIssue')}
          </span>
        ),
    },
  ];

  return (
    <section className="payments" aria-label={t('b2cBilling.platform.title')} data-testid="platform-payments">
      <ErrorAlert error={payments.error ?? error} />
      {summary ? (
        <div className="stat-cards">
          <Tile title={t('b2cBilling.platform.tiles.active')} value={summary.active.toLocaleString(locale)} sub={t('b2cBilling.platform.tiles.activeSub', { monthly: summary.monthly, yearly: summary.yearly })} testId="payments-tile-active" />
          <Tile
            title={t('b2cBilling.platform.tiles.taken', { month: monthName(summary.month, locale) })}
            value={formatTry(summary.takenKurus, locale, true)}
            sub={t('b2cBilling.platform.tiles.takenSub', { count: summary.takenCount })}
            testId="payments-tile-taken"
          />
          <Tile title={t('b2cBilling.platform.tiles.retrying')} value={summary.retrying.toLocaleString(locale)} sub={t('b2cBilling.platform.tiles.retryingSub')} testId="payments-tile-retrying" />
          <Tile
            title={t('b2cBilling.platform.tiles.cancelled', { month: monthName(summary.month, locale) })}
            value={summary.cancelled.toLocaleString(locale)}
            sub={t('b2cBilling.platform.tiles.cancelledSub')}
            testId="payments-tile-cancelled"
          />
        </div>
      ) : (
        <Skeleton block />
      )}
      <div className="dt-toolbar">
        <FilterChips
          label={t('b2cBilling.platform.filters')}
          value={status ?? ('all' as const)}
          onChange={(value) => list.set({ status: value && value !== 'all' ? value : null })}
          chips={[
            { value: 'all' as const, label: t('b2cBilling.platform.chips.all'), ...(data ? { count: data.counts.all } : {}) },
            { value: 'paid' as const, label: t('b2cBilling.platform.chips.paid'), ...(data ? { count: data.counts.paid } : {}) },
            { value: 'failed' as const, label: t('b2cBilling.platform.chips.failed'), tone: 'danger', ...(data ? { count: data.counts.failed } : {}) },
            { value: 'refunded' as const, label: t('b2cBilling.platform.chips.refunded'), tone: 'warning', ...(data ? { count: data.counts.refunded } : {}) },
          ]}
        />
        <span className="dt-toolbar__end">
          <FilterMenu
            label={t('b2cBilling.platform.month')}
            value={month}
            allLabel={summary ? monthName(summary.month, locale) : t('b2cBilling.platform.thisMonth')}
            options={recentMonths().map((m) => ({ value: m, label: monthName(m, locale) }))}
            onChange={(value) => list.set({ month: value })}
            testId="payments-month"
          />
          <SearchBox value={list.get('q') ?? ''} onChange={(q) => list.set({ q })} placeholder={t('b2cBilling.platform.search')} testId="payments-search" />
        </span>
      </div>
      {data ? (
        <DataTable
          label={t('b2cBilling.platform.title')}
          columns={columns}
          rows={data.rows}
          rowKey={(row) => row.id}
          rowTestId={(row) => `payment-${row.id}`}
          loading={payments.isFetching}
          empty={t('b2cBilling.platform.empty')}
          rowMenu={(row) => (
            <Menu portal align="right" label={t('b2cBilling.platform.actions', { name: row.name })} trigger={<Icons.more size={18} />} triggerClassName="ui-icon-button" data-testid="payment-menu">
              {row.invoice ? (
                <MenuItem onClick={() => setNumbering(row)} data-testid="payment-number">
                  {row.invoice.number ? t('b2cBilling.platform.changeNumber') : t('b2cBilling.platform.enterNumber')}
                </MenuItem>
              ) : null}
              {row.invoice ? (
                <MenuItem
                  onClick={() => {
                    setUploading(row);
                    file.current?.click();
                  }}
                  data-testid="payment-pdf"
                >
                  {row.invoice.hasPdf ? t('b2cBilling.platform.replacePdf') : t('b2cBilling.platform.uploadPdf')}
                </MenuItem>
              ) : null}
              {row.invoice?.hasPdf ? (
                <MenuItem onClick={() => window.open(`/api/platform/invoices/${row.invoice?.id}/pdf`, '_blank', 'noopener')} data-testid="payment-pdf-download">
                  {t('b2cBilling.platform.downloadPdf')}
                </MenuItem>
              ) : null}
              {row.status === 'paid' && providerOpen ? (
                <MenuItem danger onClick={() => setRefunding(row)} data-testid="payment-refund">
                  {t('b2cBilling.platform.refund')}
                </MenuItem>
              ) : null}
            </Menu>
          )}
          footer={<Pagination page={list.page} size={list.size} total={data.total} noun={t('b2cBilling.platform.noun')} onPage={(page) => list.set({ page: String(page) })} onSize={(size) => list.set({ size: String(size) })} />}
          data-testid="payments-table"
        />
      ) : null}
      <p className="payments__note">{t('b2cBilling.platform.note')}</p>
      <input
        ref={file}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        data-testid="payment-pdf-input"
        onChange={(e) => {
          const chosen = e.target.files?.[0];
          e.target.value = '';
          if (chosen && uploading) void upload(uploading, chosen);
        }}
      />
      {numbering?.invoice ? <NumberDialog row={numbering} onClose={() => setNumbering(null)} onDone={refresh} /> : null}
      {refunding ? <RefundDialog row={refunding} onClose={() => setRefunding(null)} onDone={refresh} /> : null}
    </section>
  );
}

function NumberDialog({ row, onClose, onDone }: { row: PlatformPaymentRowDto; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(row.invoice?.number ?? '');
  const save = useMutation({
    mutationFn: () => api('PUT', `/api/platform/invoices/${row.invoice?.id}/number`, { number: value.trim() }),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });
  const valid = /^[A-Za-z0-9][A-Za-z0-9-]{0,39}$/.test(value.trim());
  return (
    <Dialog
      title={t('b2cBilling.platform.numberTitle')}
      onClose={onClose}
      closeLabel={t('common.close')}
      size="sm"
      data-testid="invoice-number-dialog"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => save.mutate()} disabled={!valid} busy={save.isPending} data-testid="invoice-number-save">
            {t('common.save')}
          </Button>
        </>
      }
    >
      <p className="muted">{t('b2cBilling.platform.numberHint', { name: row.name })}</p>
      <Field label={t('b2cBilling.platform.number')}>
        {(f) => (
          <TextInput id={f.id} aria-describedby={f.describedBy} aria-invalid={f.invalid || undefined} value={value} onChange={(e) => setValue(e.target.value)} placeholder="OPY2026000000412" autoFocus data-testid="invoice-number-input" />
        )}
      </Field>
      <ErrorAlert error={save.error} />
    </Dialog>
  );
}

function RefundDialog({ row, onClose, onDone }: { row: PlatformPaymentRowDto; onClose: () => void; onDone: () => void }) {
  const { t, i18n } = useTranslation();
  const [reason, setReason] = useState('');
  const refund = useMutation({
    mutationFn: () => api('POST', `/api/platform/payments/${row.id}/refund`, { reason: reason.trim() }),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });
  return (
    <Dialog
      title={t('b2cBilling.platform.refundTitle')}
      onClose={onClose}
      closeLabel={t('common.close')}
      size="sm"
      role="alertdialog"
      data-testid="refund-dialog"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={() => refund.mutate()} disabled={reason.trim().length < 3} busy={refund.isPending} data-testid="refund-confirm">
            {t('b2cBilling.platform.refund')}
          </Button>
        </>
      }
    >
      <p>{t('b2cBilling.platform.refundBody', { name: row.name, amount: formatTry(row.amountKurus, i18n.language) })}</p>
      <Field label={t('b2cBilling.platform.refundReason')}>
        {(f) => (
          <TextInput id={f.id} aria-describedby={f.describedBy} aria-invalid={f.invalid || undefined} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus data-testid="refund-reason" />
        )}
      </Field>
      <ErrorAlert error={refund.error} />
    </Dialog>
  );
}
